import { differenceInCalendarWeeks, parseISO } from 'date-fns';

/**
 * OTRIX Play — series semanales para los sparklines y el badge de tendencia de los tiles
 * ("Resultados registrados", "Tipos de ejercicio"...).
 *
 * Todo se deriva en el cliente del `created_at` de `myPlayResults`: no hay endpoint de
 * histórico semanal y no hacía falta uno.
 */

export const WEEKS = 8;

/**
 * Reparte items en las últimas `weeks` semanas. Devuelve un array de longitud fija donde el
 * índice 0 es la semana más antigua y el último la semana en curso, de modo que el sparkline
 * mantenga el mismo eje temporal aunque haya semanas sin actividad.
 *
 * `weekStartsOn: 1` = lunes (ISO-8601), igual que el `day_of_week` de la rutina.
 */
export const weeklyBuckets = (items, getDate, weeks = WEEKS) => {
  const buckets = Array.from({ length: weeks }, () => []);
  const now = new Date();

  (items || []).forEach((item) => {
    const raw = getDate(item);
    if (!raw) return;
    let date;
    try {
      date = parseISO(raw);
    } catch {
      return;
    }
    if (Number.isNaN(date.getTime())) return;

    // 0 = semana en curso, 1 = la anterior... -> índice desde el final del array.
    const back = differenceInCalendarWeeks(now, date, { weekStartsOn: 1 });
    if (back < 0 || back >= weeks) return;
    buckets[weeks - 1 - back].push(item);
  });

  return buckets;
};

/** Serie de conteos por semana (para el sparkline). */
export const countSeries = (items, getDate, weeks = WEEKS) =>
  weeklyBuckets(items, getDate, weeks).map((bucket) => bucket.length);

/** Serie de valores distintos por semana — p. ej. cuántos ejercicios diferentes se tocaron. */
export const distinctSeries = (items, getDate, getKey, weeks = WEEKS) =>
  weeklyBuckets(items, getDate, weeks).map((bucket) => new Set(bucket.map(getKey).filter(Boolean)).size);

/**
 * Tendencia de la semana en curso contra la anterior, lista para `<StatCard trend trendValue>`.
 *
 * Devuelve `neutral` (sin flecha de subida/bajada) cuando no hay base de comparación: sin
 * semana previa el porcentaje sería infinito, y "↑∞%" no le dice nada a nadie.
 */
export const trendOf = (series) => {
  if (!series || series.length < 2) return null;

  const current = series[series.length - 1];
  const previous = series[series.length - 2];

  if (!previous) return current > 0 ? { trend: 'neutral', trendValue: '—' } : null;

  const delta = Math.round(((current - previous) / previous) * 100);
  if (delta === 0) return { trend: 'neutral', trendValue: '0%' };

  return { trend: delta > 0 ? 'up' : 'down', trendValue: `${delta > 0 ? '+' : ''}${delta}%` };
};
